/* ==========================================================================
   Meet The Montigo You — Quiz controller (bl_personality-test.liquid)

   Flow: cover → name → gender → q1..q8 → redirect to result page.

   --------------------------------------------------------------------------
   SCORING SYSTEM (source: "Montigo_Personality Test_Deck.pdf", pages 3–4)
   --------------------------------------------------------------------------
   • 8 questions, 2 per MBTI dimension:
       EI  Energy    E (Social)   vs I (Reserved)   — Q1, Q2
       NS  Lifestyle N (Explorer) vs S (Homebody)   — Q3, Q4
       TF  Decision  T (Planner)  vs F (Spontaneous)— Q5, Q6
       PJ  Style     P (Bold)     vs J (Calm)       — Q7, Q8
     (The deck maps the letters this way even where it differs from
      textbook MBTI — we follow the deck.)

   • Every answer is authored with:
       side   — which trait it pulls toward ('first' = E/N/T/P, 'second' = I/S/F/J)
       weight — 0–100, how strongly it pulls toward that side

   • For scoring we normalise every answer to a single axis:
       pctFirst = (side == 'first') ? weight : 100 - weight
     i.e. "how many % toward the FIRST trait of the dimension".
     Deck example: Q1 answer "30% toward E"  → pctFirst = 30
                   Q2 answer "80% toward I"  → pctFirst = 20
                   average  = 25  → 25 < 50 → I  ✓ (matches the deck's outcome)

   • Per dimension: average the 2 answers' pctFirst.
       avg > 50  → first trait (E/N/T/P)
       avg < 50  → second trait (I/S/F/J)
       avg == 50 → second trait (documented tie-break; see below)

   • TIE PREVENTION: a tie only happens when the two pctFirst values sum to
     exactly 100. The preset weights in the page template are chosen so that
     the possible pctFirst values are {85, 63, 13, 39} — no pair of these sums
     to 100, so ties are impossible with the shipped presets.
     If a merchant later edits weights in the theme editor and creates a
     tie-able pair, we still resolve deterministically (second trait) and log
     a console warning so it is easy to notice during QA.

   • The 4 dimension letters combine (in EI→NS→TF→PJ order) into one of the
     16 types, e.g. E+N+F+P = "ENFP" → The Vibrant Explorer.
   --------------------------------------------------------------------------

   Persistence: sessionStorage (per user's spec — cleared when the tab
   closes). Key + shape documented in MTY_STORAGE below.
   ========================================================================== */

(function () {
  'use strict';

  var MTY_STORAGE = {
    key: 'mty_quiz_v1',
    /* Stored shape:
       {
         name:    "Sarah",
         gender:  "male" | "female",
         answers: [{ dim:"EI", pctFirst: 85 }, ...],   // one per answered question
         type:    "ENFP",                              // present once quiz completed
         ts:      1719900000000                        // completion timestamp
       } */
    read: function () {
      try {
        return JSON.parse(sessionStorage.getItem(this.key)) || {};
      } catch (e) {
        return {};
      }
    },
    write: function (data) {
      try {
        sessionStorage.setItem(this.key, JSON.stringify(data));
      } catch (e) {
        /* Safari private mode etc. — quiz still works in-memory,
           only the result page personalisation is lost. */
        console.warn('[MTY] sessionStorage unavailable:', e);
      }
    },
    clear: function () {
      try {
        sessionStorage.removeItem(this.key);
      } catch (e) { /* noop */ }
    }
  };

  // Expose for the result/all sections (they share this file's storage shape).
  window.MTY_STORAGE = MTY_STORAGE;

  var root = document.querySelector('[data-mty-test]');
  if (!root) return;

  var resultUrl = root.getAttribute('data-result-url') || '/pages/meet-the-montigo-you-result';

  var screens = Array.prototype.slice.call(root.querySelectorAll('[data-screen]'));
  var screensTrack = root.querySelector('.mty__screens');
  var backBtn = root.querySelector('[data-mty-back]');
  var progressFill = root.querySelector('[data-mty-progress]');
  var stepBadge = root.querySelector('[data-mty-step]');
  var topbar = root.querySelector('[data-mty-topbar]');

  var questionScreens = screens.filter(function (s) {
    return s.getAttribute('data-screen').indexOf('question') === 0;
  });

  var state = {
    index: 0,             // current screen index
    name: '',
    gender: '',
    answers: {}           // screenIndex -> { dim, pctFirst }
  };

  /* ------------------------------------------------------------------ */
  /* Name + Gender sub-steps (same physical "info" screen/page — see       */
  /* sections/bl_personality-test.liquid). Only .mty-info__form + its      */
  /* footer swap; back/logo/decor/bubble stay mounted throughout.          */
  /* ------------------------------------------------------------------ */

  var infoStep = 'name';
  var infoSteps = Array.prototype.slice.call(root.querySelectorAll('[data-info-step]'));

  function setInfoStepActive(step) {
    infoSteps.forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-info-step') === step);
    });
  }

  // initial=true = first time the info page is entered (form waits for the
  // bubble's pop to finish, per the original choreography); initial=false =
  // swapping name<->gender within the same page (form pops immediately, the
  // shared bubble is left alone).
  function playInfoStepEnter(step, initial) {
    var stepEl = infoSteps.filter(function (el) {
      return el.getAttribute('data-info-step') === step;
    })[0];
    if (!stepEl) return;
    stepEl.classList.remove('mty-info__step--enter', 'mty-info__step--enter-initial');
    void stepEl.offsetWidth; // force reflow so the animation restarts
    stepEl.classList.add(initial ? 'mty-info__step--enter-initial' : 'mty-info__step--enter');
  }

  function showInfoStep(step) {
    infoStep = step;
    setInfoStepActive(step);
    playInfoStepEnter(step, false);
  }

  /* ------------------------------------------------------------------ */
  /* Desktop-only info/quiz backdrop overlays (.mty-info-bg /              */
  /* .mty-quiz-bg). The shared background art lives in the always-mounted  */
  /* .mty-test-bgfx layer, and the character bubbles/avatars were removed  */
  /* (client 10/07/2026), so swapping is a plain class toggle — no         */
  /* collapse/grow choreography left to run.                               */
  /* ------------------------------------------------------------------ */

  var currentBackdrop = null; // 'info' | 'quiz' | null

  function setBackdrop(type) {
    if (currentBackdrop === type) return;
    currentBackdrop = type;
    root.classList.toggle('mty--on-info', type === 'info');
    root.classList.toggle('mty--on-quiz', type === 'quiz');
  }

  /* ------------------------------------------------------------------ */
  /* Screen navigation                                                    */
  /* ------------------------------------------------------------------ */

  function show(index) {
    state.index = Math.max(0, Math.min(index, screens.length - 1));
    screens.forEach(function (screen, i) {
      screen.classList.toggle('is-active', i === state.index);
    });

    // Slide the horizontal track to the active screen (scroll left/right).
    if (screensTrack) {
      screensTrack.style.transform = 'translateX(' + (-state.index * 100) + '%)';
    }

    var current = screens[state.index];
    var screenName = current.getAttribute('data-screen');
    var isCover = screenName === 'cover';
    var isInfo = screenName === 'info';
    var isQuiz = screenName.indexOf('question') === 0;
    var qIndex = questionScreens.indexOf(current);

    // Shared topbar (progress + step badge) is for the quiz only. The cover
    // has no chrome; the info screens carry their own back button + logo.
    if (topbar) topbar.classList.toggle('mty-hidden', isCover || isInfo);

    // Desktop-only landing cover replaces the mobile stage while on "cover"
    // (see .mty--on-cover in assets/bl_personality.css).
    root.classList.toggle('mty--on-cover', isCover);

    // Desktop-only backdrop behind the (still-visible) mobile card on the
    // info + quiz screens (see .mty--on-info / .mty--on-quiz in
    // assets/bl_personality.css) — setBackdrop() animates the avatar
    // bubbles when swapping directly between the two (see above).
    setBackdrop(isInfo ? 'info' : (isQuiz ? 'quiz' : null));

    // The load-time fit is a no-op if the stage was hidden then (desktop
    // cover hides it entirely) — retry once the quiz is actually visible.
    if (isQuiz && !questionsFitted) fitQuestionText();

    // (Re-)trigger the bubble + current step's form entrance animation every
    // time the info page becomes active, since Back/Next can revisit it.
    // infoStep is NOT reset here — arriving back from a question should show
    // whichever step (name/gender) was last active, not restart at name.
    if (isInfo) {
      current.classList.remove('mty-info--enter');
      void current.offsetWidth; // force reflow so the animation restarts
      current.classList.add('mty-info--enter');
      setInfoStepActive(infoStep);
      playInfoStepEnter(infoStep, true);
    }

    if (progressFill) {
      var pct = qIndex >= 0 ? ((qIndex + 1) / questionScreens.length) * 100 : 0;
      progressFill.style.width = pct + '%';
    }
    if (stepBadge) {
      stepBadge.textContent = qIndex >= 0 ? String(qIndex + 1) : '';
      stepBadge.classList.toggle('mty-hidden', qIndex < 0);
      // The badge is one shared element reused across every question screen
      // (only its text changes above), so its per-question colour (set in
      // the block's "Step badge colour" setting) has to be applied here too.
      stepBadge.style.background = qIndex >= 0 ? (current.getAttribute('data-badge-color') || '') : '';
    }
  }

  function next() {
    show(state.index + 1);
  }

  // Shared topbar back button (question screens only — the info page has its
  // own back button handled separately below since it needs to step name<-
  // gender before actually leaving the page).
  root.querySelectorAll('[data-mty-back]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      show(state.index - 1);
    });
  });

  var infoBackBtn = root.querySelector('[data-mty-info-back]');
  if (infoBackBtn) {
    infoBackBtn.addEventListener('click', function () {
      if (infoStep === 'gender') {
        showInfoStep('name');
      } else {
        show(state.index - 1);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cover                                                                */
  /* ------------------------------------------------------------------ */

  // Two CTAs share this attribute: the mobile cover button and the
  // desktop-only landing cover button (assets/bl_personality.css .mty-landing).
  root.querySelectorAll('[data-mty-start]').forEach(function (btn) {
    btn.addEventListener('click', next);
  });

  /* ------------------------------------------------------------------ */
  /* Name screen                                                          */
  /* ------------------------------------------------------------------ */

  var nameInput = root.querySelector('[data-mty-name-input]');
  var nameNext = root.querySelector('[data-mty-name-next]');

  function syncNameButton() {
    if (nameNext) nameNext.disabled = !(nameInput && nameInput.value.trim().length > 0);
  }

  if (nameInput) {
    nameInput.addEventListener('input', syncNameButton);
    nameInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && nameInput.value.trim()) {
        e.preventDefault();
        commitName();
      }
    });
    syncNameButton();
  }

  function commitName() {
    state.name = nameInput ? nameInput.value.trim() : '';
    persistProgress();
    showInfoStep('gender');
  }

  if (nameNext) nameNext.addEventListener('click', commitName);

  /* ------------------------------------------------------------------ */
  /* Gender screen                                                        */
  /* ------------------------------------------------------------------ */

  var genderButtons = Array.prototype.slice.call(root.querySelectorAll('[data-mty-gender]'));
  var genderNext = root.querySelector('[data-mty-gender-next]');

  genderButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.gender = btn.getAttribute('data-mty-gender');
      genderButtons.forEach(function (b) {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      if (genderNext) genderNext.disabled = false;
    });
  });

  if (genderNext) {
    genderNext.disabled = true;
    genderNext.addEventListener('click', function () {
      persistProgress();
      next();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Question screens                                                     */
  /* ------------------------------------------------------------------ */

  questionScreens.forEach(function (screen) {
    var answers = Array.prototype.slice.call(screen.querySelectorAll('[data-mty-answer]'));
    var nextBtn = screen.querySelector('[data-mty-question-next]');
    var dim = screen.getAttribute('data-dimension'); // "EI" | "NS" | "TF" | "PJ"

    if (nextBtn) nextBtn.disabled = true;

    answers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        answers.forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });

        // Normalise to "% toward the first trait" — see scoring notes above.
        var side = btn.getAttribute('data-side');           // 'first' | 'second'
        var weight = parseInt(btn.getAttribute('data-weight'), 10) || 0;
        var pctFirst = side === 'first' ? weight : 100 - weight;

        state.answers[screens.indexOf(screen)] = { dim: dim, pctFirst: pctFirst };
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var isLast = questionScreens.indexOf(screen) === questionScreens.length - 1;
        if (isLast) {
          finishQuiz();
        } else {
          next();
        }
      });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Scoring + completion                                                 */
  /* ------------------------------------------------------------------ */

  // First/second trait letters per dimension, in type-code order.
  var DIMENSIONS = [
    { dim: 'EI', first: 'E', second: 'I' },
    { dim: 'NS', first: 'N', second: 'S' },
    { dim: 'TF', first: 'T', second: 'F' },
    { dim: 'PJ', first: 'P', second: 'J' }
  ];

  function computeType() {
    var answers = Object.keys(state.answers).map(function (k) {
      return state.answers[k];
    });

    return DIMENSIONS.map(function (d) {
      var scores = answers
        .filter(function (a) { return a.dim === d.dim; })
        .map(function (a) { return a.pctFirst; });

      var avg = scores.reduce(function (sum, v) { return sum + v; }, 0) / (scores.length || 1);

      if (avg === 50) {
        // Should be unreachable with the shipped preset weights (see header
        // comment). Deterministic fallback: second trait wins.
        console.warn('[MTY] Tie on dimension ' + d.dim + ' — check answer weights in the theme editor.');
      }
      return avg > 50 ? d.first : d.second;
    }).join('');
  }

  function persistProgress() {
    var data = MTY_STORAGE.read();
    data.name = state.name || data.name || '';
    data.gender = state.gender || data.gender || '';
    MTY_STORAGE.write(data);
  }

  function finishQuiz() {
    var type = computeType();
    MTY_STORAGE.write({
      name: state.name,
      gender: state.gender,
      answers: Object.keys(state.answers).map(function (k) { return state.answers[k]; }),
      type: type,
      ts: Date.now()
    });

    // No query params on purpose: the user's own result renders in Mode A
    // straight from sessionStorage. Shared links (with ?type=&gender=) are
    // generated on the result page instead.
    window.location.href = resultUrl;
  }

  /* ------------------------------------------------------------------ */
  /* Question font fitting                                                */
  /* ------------------------------------------------------------------ */

  // .mty-quiz__question is a FIXED 2-line block (82.8px — see the CSS), so
  // the answers + NEXT start on the same line on every screen. Questions too
  // long for 2 lines at the design's 36px get stepped down until they fit,
  // matching Figma, where the longer question titles are simply set smaller
  // inside the same fixed frame.
  var questionsFitted = false;

  function fitQuestionText() {
    questionsFitted = false;
    questionScreens.forEach(function (screen) {
      var q = screen.querySelector('.mty-quiz__question');
      if (!q) return;
      q.style.fontSize = ''; // re-fit from the stylesheet size (36px)
      if (!q.clientHeight) return; // hidden (e.g. desktop cover) — can't measure
      var size = parseFloat(window.getComputedStyle(q).fontSize);
      // +1 tolerates sub-pixel rounding of the 82.8px block.
      while (q.scrollHeight > q.clientHeight + 1 && size > 22) {
        size -= 2;
        q.style.fontSize = size + 'px';
      }
      questionsFitted = true;
    });
  }

  fitQuestionText();
  // Re-fit once the real webfont swaps in (metrics differ from the fallback)
  // and if the viewport changes (mobile line wraps differ from desktop).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitQuestionText);
  }
  window.addEventListener('resize', fitQuestionText);

  /* ------------------------------------------------------------------ */
  /* Pre-load later screens' imagery (client 13/07/2026)                  */
  /* ------------------------------------------------------------------ */

  /* The slide track keeps every screen in the DOM but x-offscreen, so the
     info decors / question illustrations (loading="lazy") never fetch until
     their screen slides in — the visitor saw blank slots with images popping
     in a beat later. Once the page (cover art included) has finished
     loading, flip them all to eager and decode, so the fetches happen
     quietly behind the cover and every screen arrives fully painted. */
  function preloadScreenImages() {
    root.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.setAttribute('loading', 'eager'); // triggers the deferred fetch
      if (img.decode) img.decode().catch(function () { /* decodes on paint instead */ });
    });
  }

  if (document.readyState === 'complete') {
    preloadScreenImages();
  } else {
    window.addEventListener('load', preloadScreenImages);
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  // Retaking the quiz always starts fresh.
  show(0);
})();
